import React from 'react';
import Layout from '../../components/Layout';
import {
    Search, ArrowUpRight, ArrowDownLeft, Download,
    Calendar, RefreshCw
} from 'lucide-react';

import apiService from '../../services/apiService';

const Transactions = () => {
    // Mock Transactions
    const [transactions, setTransactions] = React.useState([]);
    const [fromDate, setFromDate] = React.useState('');
    const [toDate, setToDate] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.getAdminTransactions({ from: fromDate, to: toDate });
            if (response.success) {
                setTransactions(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch transactions", error);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
                        <p className="text-gray-500 text-sm mt-1">Global financial activity log</p>
                    </div>
                    <button className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium flex items-center gap-2 shadow-sm">
                        <Download size={18} /> Export CSV
                    </button>
                </div>

                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search transaction ID or user..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer"
                            />
                        </div>
                        <span className="text-gray-300 text-xs font-bold">to</span>
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer"
                            />
                        </div>

                        <div className="h-8 w-[1px] bg-gray-200 hidden md:block"></div>

                        <div className="flex gap-2">
                            <select className="px-3 py-1.5 border border-transparent bg-slate-50 text-[11px] font-black uppercase tracking-widest rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
                                <option>All Types</option>
                                <option>Credit</option>
                                <option>Debit</option>
                            </select>
                        </div>

                        <button
                            onClick={fetchData}
                            className="px-4 py-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-lg flex items-center gap-2"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                            Apply
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 text-[10px] font-black uppercase tracking-widest">
                                <tr>
                                    <th className="px-6 py-4">Transaction ID</th>
                                    <th className="px-6 py-4">User</th>
                                    <th className="px-6 py-4">Source</th>
                                    <th className="px-6 py-4 text-center">Wallet</th>
                                    <th className="px-6 py-4">Amount</th>
                                    <th className="px-6 py-4">Status & Details</th>
                                    <th className="px-6 py-4 text-right">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {transactions.map((txn) => (
                                    <tr key={txn.id} className="hover:bg-gray-50/50 group transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="font-mono text-[10px] text-gray-400 bg-gray-50 px-2 py-1 rounded border border-gray-100">{txn.id}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-black text-gray-900 text-sm tracking-tight">{txn.user}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <div className={`flex items - center gap - 1.5 text - [10px] font - black uppercase tracking - wider ${txn.type === 'Credit' ? 'text-green-600' : 'text-red-500'} `}>
                                                    {txn.type === 'Credit' ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}
                                                    {txn.category}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px - 2 py - 1 rounded text - [9px] font - black uppercase tracking - tighter ${txn.wallet === 'Shopping' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'} `}>
                                                {txn.wallet}
                                            </span>
                                        </td>
                                        <td className={`px - 6 py - 4 font - black text - sm tracking - tighter ${txn.type === 'Credit' ? 'text-green-600' : 'text-red-600'} `}>
                                            {txn.type === 'Credit' ? '+' : '-'} ₹{parseFloat(txn.amount).toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <span className={`w - fit px - 2 py - 0.5 rounded - full text - [9px] font - black tracking - widest uppercase ${txn.status === 'Success' ? 'bg-green-100 text-green-700' :
                                                    txn.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                                        'bg-red-100 text-red-700'
                                                    } `}>
                                                    {txn.status}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-bold line-clamp-1">{txn.description}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="text-[10px] font-black text-gray-900 tabular-nums uppercase tracking-tighter">{txn.date}</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Transactions;
